import { Broadcast } from 'features/broadcast/broadcastsSlice';
import { Event } from 'features/events/eventsSlice';
import { isEqual } from 'lodash';
import dayjs, { Dayjs } from 'utilities/dayjs';
import { getState } from 'store';

export interface EventModel extends Event {
  isUpcoming(): boolean;
  // milliseconds until the event starts (0 when not upcoming)
  durationToEvent(): number;
  isIn24hrs(): boolean;
  isVisibleOnDashboard(): boolean;
  isOnlyVisibleToCreator(): boolean;
  isOverdue(): boolean;
  parsedStartsAt?: Dayjs | null;
  parsedStartedAt?: Dayjs | null;
  parsedLocalStartsAt?: Dayjs | null;
  parsedLocalStartedAt?: Dayjs | null;
  parsedEndsAt?: Dayjs | null;
  parsedEndedAt?: Dayjs | null;
  parsedLocalEndsAt?: Dayjs | null;
  parsedLocalEndedAt?: Dayjs | null;
  latestBroadcastStartedAt?: Dayjs | null;
  isPrivate(): boolean;
  isActive(): boolean;
  isLive(): boolean;
  isEnded(): boolean;
  isListed(): boolean;
  isVisibleOnChannel(): boolean;
  isUpcomingOrLive(): boolean;
  isAdhoc(): boolean;
  isFallback(): boolean;
  isScheduledRecording(): boolean;
  inner: Event;
}

export const compareEvents = (
  event1: EventModel | null,
  event2: EventModel | null
): boolean => {
  if (!event1 && !event2) {
    return isEqual(event1, event2);
  }

  return isEqual(event1?.inner, event2?.inner);
};

export const compareByDate = (
  event1: EventModel,
  event2: EventModel
): number => {
  if (!event1.parsedStartsAt && !event2.parsedStartsAt) {
    return 0;
  }

  if (!event1.parsedStartsAt) {
    return -1;
  }

  if (!event2.parsedStartsAt) {
    return 1;
  }

  if (event1.parsedStartsAt.isAfter(event2.parsedStartsAt)) {
    return 1;
  }

  if (event1.parsedStartsAt.isBefore(event2.parsedStartsAt)) {
    return -1;
  }

  return 0;
};

export const compareByLatestBroadcastStartedAt = (
  event1: EventModel,
  event2: EventModel
): number => {
  if (!event1.latestBroadcastStartedAt && !event2.latestBroadcastStartedAt) {
    return 0;
  }

  if (!event1.latestBroadcastStartedAt) {
    return 1;
  }

  if (!event2.latestBroadcastStartedAt) {
    return -1;
  }

  if (
    event1.latestBroadcastStartedAt.isBefore(event2.latestBroadcastStartedAt)
  ) {
    return 1;
  }

  if (
    event1.latestBroadcastStartedAt.isAfter(event2.latestBroadcastStartedAt)
  ) {
    return -1;
  }

  return 0;
};

export const compareByFallback = (
  event1: EventModel,
  event2: EventModel
): number => {
  return Number(event1.fallback) - Number(event2.fallback);
};

// Single source of truth for "is this event visible on the public channel".
// Only `public` events are listed; `unlisted`, `only_me` (and anything else)
// are not. Kept as a standalone helper so broadcast-level code can reason about
// visibility without constructing an EventModel.
export const isAccessLevelListed = (
  accessLevel: string | null | undefined
): boolean => {
  return accessLevel === 'public';
};

export const EventModel = (event: Event): EventModel => {
  const parsedAttrs: Partial<EventModel> = {};

  if (event.startsAt) {
    parsedAttrs.parsedStartsAt = dayjs(event.startsAt).tz(event.timezone);

    parsedAttrs.parsedLocalStartsAt = dayjs(event.startsAt);
  }

  if (event.startedAt) {
    parsedAttrs.parsedStartedAt = dayjs(event.startedAt).tz(event.timezone);

    parsedAttrs.parsedLocalStartedAt = dayjs(event.startedAt);
  }

  if (event.endsAt) {
    parsedAttrs.parsedEndsAt = dayjs(event.endsAt).tz(event.timezone);

    parsedAttrs.parsedLocalEndsAt = dayjs(event.endsAt);
  }

  if (event.endedAt) {
    parsedAttrs.parsedEndedAt = dayjs(event.endedAt).tz(event.timezone);

    parsedAttrs.parsedLocalEndedAt = dayjs(event.endedAt);
  }

  return {
    ...parsedAttrs,
    ...event,
    inner: event,
    isUpcoming() {
      if (!this.parsedEndsAt || this.isLive()) return false;

      const nowInTimezone = dayjs().tz(event.timezone);
      return this.parsedEndsAt.isAfter(nowInTimezone);
    },

    isListed() {
      return isAccessLevelListed(this.inner.accessLevel);
    },

    // Whether this event may surface as the channel's live/upcoming event.
    // The fallback is the channel's public always-on source, so it is always
    // visible regardless of accessLevel (Rails historically copied the
    // fallback collection's access level onto it, so it can be 'unlisted').
    // Mirrors the fallback exemption in the broadcast layer
    // (features/broadcast/selectors.ts).
    isVisibleOnChannel() {
      return this.isFallback() || this.isListed();
    },

    isIn24hrs() {
      const cutOff = dayjs().tz(event.timezone).add(1, 'day');

      return (
        !this.isLive() &&
        !!this.parsedStartsAt &&
        this.parsedStartsAt.isBefore(cutOff)
      );
    },

    isOnlyVisibleToCreator() {
      const {
        currentUser: { me }
      } = getState();
      return (
        this.inner.accessLevel === 'only_me' &&
        me?.id === this.inner.broadcasterId
      );
    },

    isVisibleOnDashboard() {
      const cutOff = dayjs().tz(event.timezone).add(2, 'day');

      return (
        !this.isLive() &&
        !!this.parsedStartsAt &&
        this.parsedStartsAt.isBefore(cutOff)
      );
    },

    durationToEvent() {
      if (!this.parsedStartsAt) return 0;
      if (!this.isUpcoming()) return 0;

      const nowInTimezone = dayjs().tz(event.timezone);

      return this.parsedStartsAt.diff(nowInTimezone);
    },

    isOverdue() {
      if (!this.parsedStartsAt) return false;

      const nowInTimezone = dayjs().tz(event.timezone);

      return this.parsedStartsAt.isBefore(nowInTimezone) && !this.isLive();
    },

    isPrivate() {
      return !!this.accesscodeEnabled;
    },

    isActive() {
      return !!this.active;
    },

    isLive() {
      const {
        broadcasts: { broadcasts }
      } = getState();

      const eventBroadcast = broadcasts.find(
        (broadcastEl: Broadcast) => broadcastEl.eventId === this.id
      );

      if (eventBroadcast && eventBroadcast.startedAt) {
        this.latestBroadcastStartedAt = dayjs(eventBroadcast.startedAt);
      }

      return this.isActive() && !!eventBroadcast?.isLive;
    },

    isUpcomingOrLive() {
      return this.isUpcoming() || this.isLive();
    },

    isEnded() {
      return !!this.endedAt;
    },

    isAdhoc() {
      return !!this.adhoc;
    },

    isFallback() {
      return !!this.fallback;
    },

    isScheduledRecording() {
      return !!this.scheduled_recording;
    }
  };
};
